/**
 * CMS API Service
 * Handles communication with the CMS backend for domain management and site publishing
 */

import { CMSDomainInfo, CMSSiteDomainInfo, CMSSiteInfo } from "../types";

export interface CMSApiConfig {
  baseUrl: string;
  apiToken: string;
  divisionId: number;
}

class CMSApiService {
  private config: CMSApiConfig | null = null;

  initialize(config: CMSApiConfig) {
    this.config = config;
  }

  isConfigured(): boolean {
    return !!(this.config?.baseUrl && this.config?.apiToken);
  }

  private ensureConfigured(): CMSApiConfig {
    if (!this.config) {
      throw new Error("CMS API not configured");
    }
    return this.config;
  }

  private getHeaders(): HeadersInit {
    const config = this.ensureConfigured();
    return {
      Authorization: `Token ${config.apiToken}`,
      division: String(config.divisionId),
      "Content-Type": "application/json",
    };
  }

  private getMultipartHeaders(): HeadersInit {
    const config = this.ensureConfigured();
    return {
      Authorization: `Token ${config.apiToken}`,
      division: String(config.divisionId),
    };
  }

  async getDomain(): Promise<CMSDomainInfo> {
    const config = this.ensureConfigured();

    const response = await fetch(`${config.baseUrl}/apis/PRX/official-account/cms/domain/`, {
      method: "GET",
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get domain: ${error}`);
    }

    return response.json();
  }

  async setDomain(domain: string): Promise<CMSDomainInfo> {
    const config = this.ensureConfigured();

    const response = await fetch(`${config.baseUrl}/apis/PRX/official-account/cms/domain/`, {
      method: "PATCH",
      headers: this.getHeaders(),
      body: JSON.stringify({ domain }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to set domain: ${error}`);
    }

    return response.json();
  }

  async removeDomain(): Promise<CMSDomainInfo> {
    const config = this.ensureConfigured();

    const response = await fetch(`${config.baseUrl}/apis/PRX/official-account/cms/domain/`, {
      method: "PATCH",
      headers: this.getHeaders(),
      body: JSON.stringify({ domain: "" }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to remove domain: ${error}`);
    }

    return response.json();
  }

  async verifyDomain(): Promise<{ success: boolean; message?: string }> {
    const config = this.ensureConfigured();

    const response = await fetch(`${config.baseUrl}/apis/PRX/official-account/cms/domain/verify/`, {
      method: "POST",
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to verify domain: ${error}`);
    }

    return response.json();
  }

  async getSubdomain(): Promise<CMSSiteDomainInfo | null> {
    const config = this.ensureConfigured();

    const response = await fetch(`${config.baseUrl}/apis/PRX/official-account/cms/domain/subdomain/`, {
      method: "GET",
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      return null;
    }

    const text = await response.text();
    if (!text || text.trim() === "") {
      return null;
    }

    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  async setSubdomain(subdomain: string): Promise<CMSSiteDomainInfo> {
    const config = this.ensureConfigured();

    const response = await fetch(`${config.baseUrl}/apis/PRX/official-account/cms/domain/subdomain/`, {
      method: "PATCH",
      headers: this.getHeaders(),
      body: JSON.stringify({ subdomain }),
    });

    const text = await response.text();

    if (!response.ok) {
      throw new Error(`Failed to set subdomain: ${text}`);
    }

    if (!text || text.trim() === "") {
      return { subdomain };
    }

    try {
      return JSON.parse(text);
    } catch {
      return { subdomain };
    }
  }

  async getSite(): Promise<CMSSiteInfo | null> {
    const config = this.ensureConfigured();

    const response = await fetch(`${config.baseUrl}/apis/PRX/official-account/cms/site/`, {
      method: "GET",
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      return null;
    }

    const text = await response.text();
    if (!text || text.trim() === "") {
      return null;
    }

    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  async setSitePublished(isPublished: boolean): Promise<CMSSiteInfo> {
    const config = this.ensureConfigured();

    const response = await fetch(`${config.baseUrl}/apis/PRX/official-account/cms/site/`, {
      method: "PATCH",
      headers: this.getHeaders(),
      body: JSON.stringify({ is_site_published: isPublished }),
    });

    const text = await response.text();

    if (!response.ok) {
      throw new Error(`Failed to update site: ${text}`);
    }

    if (!text || text.trim() === "") {
      // Fallback: return minimal info (API should return full object)
      return { is_site_published: isPublished } as CMSSiteInfo;
    }

    try {
      return JSON.parse(text) as CMSSiteInfo;
    } catch {
      return { is_site_published: isPublished } as CMSSiteInfo;
    }
  }

  async uploadSite(zipBlob: Blob): Promise<{ success: boolean; message?: string }> {
    const config = this.ensureConfigured();

    const formData = new FormData();
    formData.append("file", zipBlob, "site.zip");

    const response = await fetch(`${config.baseUrl}/apis/PRX/official-account/cms/site/upload/`, {
      method: "PUT",
      headers: this.getMultipartHeaders(),
      body: formData,
    });

    const text = await response.text();

    if (!response.ok) {
      throw new Error(`Failed to upload site: ${text}`);
    }

    if (!text || text.trim() === "") {
      return { success: true };
    }

    try {
      return JSON.parse(text);
    } catch {
      return { success: true, message: text };
    }
  }
}

export const cmsApi = new CMSApiService();
